import type { InteractiveAnswer, QuestionAnswerEntry } from '@/_shared/types';

// Pure constructors for the InteractiveAnswer sent to `chat.answerInteractive`, plus small formatters
// for the transcript summary. Kept framework-free so they are unit-testable in isolation.

export function questionAnswered(blockId: string, entries: QuestionAnswerEntry[]): InteractiveAnswer {
  return { blockId, kind: 'question', status: 'answered', entries };
}

export function questionSkipped(blockId: string): InteractiveAnswer {
  return { blockId, kind: 'question', status: 'skipped', entries: [] };
}

export function questionDismissed(blockId: string): InteractiveAnswer {
  return { blockId, kind: 'question', status: 'dismissed', entries: [] };
}

export function approvalAnswer(
  blockId: string,
  decision: 'approved' | 'rejected' | 'revised',
  comment?: string,
): InteractiveAnswer {
  const trimmed = comment?.trim();
  return { blockId, kind: 'approval', decision, ...(trimmed ? { comment: trimmed } : {}) };
}

/** Dismiss an approval (the ✕): denies the ask tool so the agent does not proceed, like a question dismiss. */
export function approvalDismissed(blockId: string): InteractiveAnswer {
  return { blockId, kind: 'approval', decision: 'dismissed' };
}

/** Render one answer entry for the transcript summary: first two items then `+N more`.
 * A multi-select's "Other" free text is a distinct item alongside the selected option labels. */
export function summarizeEntry(entry: QuestionAnswerEntry): string {
  if (entry.kind === 'skipped') return 'Skipped — no preference';
  if (entry.kind === 'manual') return entry.text;
  const items = entry.otherText !== undefined ? [...entry.labels, entry.otherText] : entry.labels;
  const [a, b, ...rest] = items;
  if (!a) return '—';
  if (!b) return a;
  const head = `${a}, ${b}`;
  return rest.length > 0 ? `${head} +${rest.length} more` : head;
}
